import { Injectable } from '@angular/core';
import {
  AbstractGraphNodeBuilder,
  DeclarativeFieldComponent,
  FieldNodeInputDefinitionList,
  GraphBuilderContext,
  GraphInputDefinition,
} from '@fmnts/declarative-forms';
import {
  FieldReference,
  FormFieldSource,
  FormNodeName,
  FormOperatorName,
  RxJsNodeName,
  RxJsOperatorNodeName,
} from '@fmnts/declarative-forms/common';
import { filter, map, OperatorFunction } from 'rxjs';

import { BankService } from '@app/core/payment/bank.service';
import { IbanService } from '@app/core/payment/iban.service';
import { SortCodeService } from '@app/core/payment/sort-code.service';
import { Bank } from '@app/core/payment/types/bank';
import { Iban } from '@app/core/payment/types/iban';
import { SortCode } from '@app/core/payment/types/sort-code';
import {
  NextFieldKey,
  NextFieldsetKey,
  nextFieldsetsWithIban,
} from '../next-form-known-fields';

enum OperatorNames {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  Iban = 'payment.iban$',
  BankFromIban = 'payment.bankFromIban$',
  BankBic = 'payment.bankBic$',
  BankName = 'payment.bankName$',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  SortCode = 'payment.sortCode$',
  BankFromSortCode = 'payment.bankFromSortCode$',
}

/**
 * Builder for payment related graph nodes
 */
@Injectable({
  providedIn: 'root',
})
export class PaymentGraphNodeBuilder extends AbstractGraphNodeBuilder<
  string,
  OperatorNames
> {
  constructor(
    private ibanService: IbanService,
    private bankService: BankService,
    private sortCodeService: SortCodeService,
  ) {
    super();

    // Provide operators
    this.addOperator(OperatorNames.Iban, () => this.iban$())
      .addOperator(OperatorNames.BankFromIban, () => this.bankFromIban$())
      .addOperator(OperatorNames.BankBic, () => this.bankBic$())
      .addOperator(OperatorNames.BankName, () => this.bankName$())
      .addOperator(OperatorNames.SortCode, () => this.sortCode$())
      .addOperator(OperatorNames.BankFromSortCode, () =>
        this.bankFromSortCode$(),
      );
  }

  /**
   * Operator to get an instance of `Iban`
   */
  private iban$(): OperatorFunction<string, Iban> {
    return (source$) =>
      source$.pipe(
        filter((iban) => iban !== null),
        map((ibanStr) => this.ibanService.fromString(ibanStr)),
      );
  }

  /**
   * Operator to find an instance of `Bank`
   */
  private bankFromIban$(): OperatorFunction<Iban, Bank | undefined> {
    return map((iban) => this.bankService.findByIban(iban)?.bank);
  }

  /**
   * Operator to get an instance of `SortCode`
   * @returns
   * formatted string (e.g. 11-11-11)
   */
  private sortCode$(): OperatorFunction<string, SortCode> {
    return (source$) =>
      source$.pipe(
        filter((sortCode) => sortCode !== null),
        map((sortCodeStr) => this.sortCodeService.fromString(sortCodeStr)),
      );
  }

  /**
   * Operator to find the Bankname from SortCodes
   */
  private bankFromSortCode$(): OperatorFunction<SortCode, Bank | undefined> {
    return map((sortCode) => this.bankService.findBySortCode(sortCode)?.bank);
  }

  /**
   * Operator to get the BIC from an instance of `Bank`
   */
  private bankBic$(): OperatorFunction<Bank, string> {
    return (bank$) => bank$.pipe(map((bank) => (bank ? bank.bic.value : '')));
  }

  /**
   * Operator to get the name from an instance of `Bank`
   */
  private bankName$(): OperatorFunction<Bank, string> {
    return (bank$) => bank$.pipe(map((bank) => (bank ? bank.name : '')));
  }

  createFakeDefinitions(
    ctx: GraphBuilderContext,
  ): FieldNodeInputDefinitionList | undefined {
    const genericComponent = ctx.injector.get(DeclarativeFieldComponent, null);
    const fieldsetKey = genericComponent?.fieldset?.key;

    // Create fake definitions for prefilling BIC / Bank name
    // for fields within fieldsets with an IBAN field
    if (nextFieldsetsWithIban.includes(fieldsetKey)) {
      // Reference the IBAN field in the same fieldset
      const ibanFieldRef: FieldReference = {
        path: [fieldsetKey, NextFieldKey.BankIbanOrNumber],
      };

      switch (genericComponent?.field.key) {
        case NextFieldKey.BankBicOrBankCode:
          return { value: this._createBicPrefill(ibanFieldRef) };
        case NextFieldKey.BankName:
          return { value: this._createBankNamePrefill(ibanFieldRef) };
      }
    }

    if (NextFieldsetKey.BankDataSortCodeUK === fieldsetKey) {
      switch (genericComponent?.field.key) {
        case 'account_bank_name':
          return fakeSortCodeBankNameDefinition;
      }
    }
  }

  /**
   * Creates the definition that will resolve the BIC from a
   * `sourceField`.
   * The source field should be an input that accepts an IBAN.
   *
   * @param sourceField Reference to the source field
   */
  private _createBicPrefill(sourceField: FieldReference): GraphInputDefinition {
    return {
      ..._valueChangesOrInitialIfPristine(sourceField),
      pipe: [
        { operator: OperatorNames.Iban },
        { operator: OperatorNames.BankFromIban },
        { operator: OperatorNames.BankBic },
        {
          operator: FormOperatorName.SetUiState,
          args: [{ pristine: true }],
        },
      ],
    };
  }

  /**
   * Creates the definition that will resolve the Bank name from a
   * `sourceField`.
   * The source field should be an input that accepts an IBAN.
   *
   * @param sourceField Reference to the source field
   */
  private _createBankNamePrefill(sourceField: FieldReference): FormFieldSource {
    return {
      ..._valueChangesOrInitialIfPristine(sourceField),
      pipe: [
        { operator: OperatorNames.Iban },
        { operator: OperatorNames.BankFromIban },
        { operator: OperatorNames.BankName },
        {
          operator: FormOperatorName.SetUiState,
          args: [{ pristine: true }],
        },
      ],
    };
  }
}

/**
 * Helper to create a graph input that emits value changes for the given
 * `sourceField`. It will also initially emit a value if the field on which
 * it is used is pristine.
 *
 * This is used so that values on next forms are restored on reloading
 * because net stores only dirty form values.
 *
 * @param sourceField
 * @returns
 */
function _valueChangesOrInitialIfPristine(
  sourceField: FieldReference,
): GraphInputDefinition {
  return {
    name: RxJsNodeName.Concat,
    options: [
      {
        name: FormNodeName.FieldValue,
        options: sourceField,
        pipe: [
          { operator: RxJsOperatorNodeName.Take, args: [{ count: 1 }] },
          {
            operator: FormOperatorName.FieldFilterUiState,
            args: ['pristine'],
          },
        ],
      },
      {
        name: FormNodeName.FieldValueChanges,
        options: sourceField,
      },
    ],
    pipe: [],
  };
}

const fakeSortCodeBankNameDefinition: FieldNodeInputDefinitionList = {
  value: {
    ..._valueChangesOrInitialIfPristine({
      path: ['sortCodesBankData', NextFieldKey.BankBicOrBankCode],
    }),
    pipe: [
      { operator: OperatorNames.SortCode },
      { operator: OperatorNames.BankFromSortCode },
      { operator: OperatorNames.BankName },
      {
        operator: FormOperatorName.SetUiState,
        args: [{ pristine: true }],
      },
    ],
  },
};
